import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock } from 'lucide-react';
import { downloadInvoicePdf, fetchInvoices, fetchMyPaymentEvents, fmtMoney, Invoice, PaymentEvent } from '../../../api/billingApi';
import { useTheme } from '../../../context/ThemeContext';
import { useBilling } from '../../../context/BillingContext';
import styles from './BillingPage.module.css';

// A display row is an invoice, plus an optional synthetic "free trial" entry
// derived from the subscription (trials create no Payment row server-side).
type Row = Invoice & { isTrial?: boolean };

const PaymentHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const { subscription } = useBilling();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<PaymentEvent[]>([]);

  useEffect(() => {
    fetchInvoices()
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
    fetchMyPaymentEvents().then((r) => setEvents(r.events)).catch(() => setEvents([]));
  }, []);

  // Surface the free trial as its own row so payment history reflects it
  // (a trial is a real billing event even though it costs nothing).
  const trialRow: Row | null = subscription?.trial_started_at
    ? {
        id: -1,
        invoice_number: null,
        pdf_available: false,
        gateway: 'stripe',
        gateway_transaction_id: null,
        amount: '0',
        currency: 'USD',
        status: 'completed',
        description: `${(subscription.plan_tier || 'Plan').replace(/\b\w/g, c => c.toUpperCase())} — 14-day free trial started`
          + (subscription.trial_ends_at ? ` (ends ${new Date(subscription.trial_ends_at).toLocaleDateString()})` : ''),
        invoice_url: null,
        created_at: subscription.trial_started_at,
        isTrial: true,
      }
    : null;
  const rows: Row[] = trialRow ? [trialRow, ...invoices] : invoices;

  const pageBg    = dark ? '#101828' : '#f2f4f7';
  const headerBg  = dark ? '#101828' : '#fff';
  const headerBorder = dark ? '#3538cd' : '#eaecf0';
  const titleColor = dark ? '#f9fafb' : '#101828';
  const backColor  = dark ? '#d0d5dd' : '#101828';
  const backBorder = dark ? '#475467' : '#101828';
  const cardBg    = dark ? '#101828' : '#fff';
  const cardBorder = dark ? '#3538cd' : '#eaecf0';
  const tableHeadBg = dark ? '#101828' : '#f9fafb';
  const rowBorder  = dark ? '#3538cd' : '#f2f4f7';
  const textMain   = dark ? '#f9fafb' : '#101828';
  const textMuted  = dark ? '#98a2b3' : '#667085';

  return (
    <div style={{ minHeight: '100vh', background: pageBg }}>
      {/* Top bar */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${headerBorder}`, background: headerBg, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={() => navigate('/billing')}
          style={{ background: 'transparent', border: `1.5px solid ${backBorder}`, borderRadius: 8, padding: '6px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', color: backColor, flexShrink: 0 }}
        >
          ← Back
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: titleColor, margin: 0, flex: 1, textAlign: 'center', fontFamily: 'var(--ds-font-display)', letterSpacing: '-0.01em' }}>
          Payment <span style={{ color: 'var(--ds-link)' }}>History</span>
        </h1>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px' }}>

        {/* Card */}
        <div style={{ background: cardBg, borderRadius: 16, border: `1.5px solid ${cardBorder}`, boxShadow: dark ? '0 2px 12px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className={styles.spinner} />
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ marginBottom: 12 }}><FileText size={48} aria-hidden /></div>
              <div style={{ fontSize: 18, fontWeight: 700, color: textMain, marginBottom: 6 }}>No payments yet</div>
              <div style={{ fontSize: 14, color: textMuted }}>Your payment records will appear here once you subscribe.</div>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.8fr 0.9fr 0.9fr 0.9fr 1.2fr', padding: '12px 20px', background: tableHeadBg, borderBottom: `1.5px solid ${cardBorder}`, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: textMuted }}>
                <span>Date</span>
                <span>Invoice · Description</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Invoice</span>
                <span>Actions</span>
              </div>
              {rows.map((inv, i) => {
                const isTrial   = !!inv.isTrial;
                const isPaid    = !isTrial && inv.status === 'completed';
                const isFailed  = inv.status === 'failed';
                const isPending = inv.status === 'pending';
                const statusColor = isTrial ? '#7a5af8' : isPaid ? '#039855' : isFailed ? '#d92d20' : isPending ? '#6172f3' : '#b54708';
                const statusBg  = isTrial
                  ? (dark ? '#4a1fb8' : '#f4f3ff')
                  : isPaid
                  ? (dark ? '#027a48' : '#ecfdf3')
                  : isFailed
                  ? (dark ? '#d92d20' : '#fef3f2')
                  : isPending
                  ? (dark ? '#101828' : '#f0f5ff')
                  : (dark ? '#b54708' : '#fffaeb');
                const statusLabel = isTrial ? 'Free Trial' : isPaid ? 'Completed' : isFailed ? 'Failed' : isPending ? 'Initiated' : inv.status;
                const rowBg = isTrial
                  ? (dark ? '#1e1b4b' : '#faf5ff')
                  : isPending
                  ? (dark ? '#101828' : '#f0f5ff')
                  : i % 2 === 0 ? cardBg : (dark ? '#101828' : '#fcfcfd');
                return (
                  <div
                    key={inv.id}
                    style={{
                      display: 'grid', gridTemplateColumns: '1.1fr 1.8fr 0.9fr 0.9fr 0.9fr 1.2fr',
                      padding: '14px 20px', borderBottom: i < rows.length - 1 ? `1px solid ${rowBorder}` : 'none',
                      fontSize: 14, color: textMain, alignItems: 'center', background: rowBg,
                    }}
                  >
                    <span style={{ color: textMuted, fontSize: 13 }}>{new Date(inv.created_at).toLocaleDateString()}</span>
                    <span style={{ fontWeight: 500 }}>
                      {inv.invoice_number && <span style={{ display: 'block', fontSize: 11, color: textMuted, fontFamily: 'ui-monospace, monospace' }}>{inv.invoice_number}</span>}
                      {inv.description ? inv.description.replace(/\s*—\s*checkout initiated$/i, '') : '—'}
                    </span>
                    <span style={{ fontWeight: 700, color: isPending ? textMuted : textMain }}>
                      {isTrial
                        ? <span style={{ color: '#7a5af8' }}>Free</span>
                        : parseFloat(inv.amount) > 0
                        ? <>{fmtMoney(inv.amount, inv.currency)} <span style={{ fontSize: 11, color: textMuted, fontWeight: 400 }}>{inv.currency.toUpperCase()}</span></>
                        : <span style={{ color: textMuted }}>—</span>}
                    </span>
                    <span>
                      <span style={{ background: statusBg, color: statusColor, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                        {statusLabel}
                      </span>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {inv.pdf_available
                        ? <button type="button" onClick={() => downloadInvoicePdf(inv.id).catch(() => {})} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: '#444ce7', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Invoice PDF ↓</button>
                        : <span style={{ color: dark ? '#475467' : '#d0d5dd' }}>—</span>}
                      {inv.invoice_url && <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: textMuted, fontSize: 12, textDecoration: 'none' }}>Gateway receipt ↗</a>}
                    </span>
                    <span>
                      {isTrial ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--ds-info-bg)', color: 'var(--ds-link)', border: '1px solid var(--ds-border)', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                          <Clock size={14} aria-hidden /> Trial started
                        </span>
                      ) : isPending ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--ds-success-bg)', color: 'var(--ds-success-text)', border: '1px solid var(--ds-border)', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                          <Clock size={14} aria-hidden /> Checkout Initiated
                        </span>
                      ) : isPaid ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: dark ? '#101828' : 'var(--ds-surface-2)', color: '#6172f3', border: '1px solid #3b82f644', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                          ✓ Payment Done
                        </span>
                      ) : isFailed ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: dark ? '#d92d20' : '#fef3f2', color: '#d92d20', border: '1px solid #dc262644', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                          ✕ Failed
                        </span>
                      ) : (
                        <span style={{ color: dark ? '#475467' : '#d0d5dd' }}>—</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Summary */}
        {!loading && rows.length > 0 && (
          <div style={{ marginTop: 20, display: 'flex', gap: 16 }}>
            {[
              { label: 'Trial',      value: trialRow ? 1 : 0,                                        color: 'var(--ds-link)', bg: 'var(--ds-info-bg)' },
              { label: 'Completed', value: invoices.filter(i => i.status === 'completed').length, color: 'var(--ds-success-text)', bg: 'var(--ds-success-bg)' },
              { label: 'Initiated',  value: invoices.filter(i => i.status === 'pending').length,   color: 'var(--ds-link)', bg: 'var(--ds-info-bg)' },
              { label: 'Failed',     value: invoices.filter(i => i.status === 'failed').length,    color: 'var(--ds-danger-text)', bg: 'var(--ds-danger-bg)' },
              { label: 'Total',      value: rows.length,                                            color: textMain,  bg: cardBg },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, background: s.bg, border: `1.5px solid ${cardBorder}`, borderRadius: 12, padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Activity (SRS #83): what happened at the gateway, including declines and refunds */}
        {events.length > 0 && (
          <div style={{ marginTop: 24 }} data-testid="payment-activity">
            <div style={{ fontSize: 14, fontWeight: 700, color: textMain, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={16} aria-hidden /> Activity</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, border: `1.5px solid ${cardBorder}`, borderRadius: 12, background: cardBg }}>
              {events.map((e) => (
                <li key={e.id} style={{ display: 'flex', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${cardBorder}`, fontSize: 13, color: textMain, alignItems: 'baseline' }}>
                  <span style={{ color: textMuted, whiteSpace: 'nowrap', minWidth: 110 }}>{e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}</span>
                  <span style={{ fontWeight: 600, minWidth: 180 }}>{e.kind_label}</span>
                  <span style={{ flex: 1, color: e.outcome === 'failed' ? 'var(--ds-danger)' : textMain }}>{e.summary}</span>
                  {e.amount && <span style={{ whiteSpace: 'nowrap' }}>{fmtMoney(e.amount, e.currency || 'USD')}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistoryPage;

